import { memo, useCallback, useMemo } from 'react';
import { FormAsyncSelectAutocompleteField } from '@dfl/mui-react-common';
import { isOptionEqualToValue } from 'utils/comparing';
import { ProviderService } from '../../services';
import { PROVIDERS_TYPES_MAP } from 'modules/inventory/product/constants/product-other-cost.enum';
import { ListItemAvatar, ListItemText } from '@mui/material';
import { AvatarMedia } from 'components/AvatarMedia';
import { getFullName } from 'utils/index';
import { IUser } from 'modules/security/users/interfaces/IUser';

type ProvidersByTypeSelectProps = {
  name: string;
  parentName?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  multiple?: boolean;
  disabled?: boolean;
  setValue?: any;
  control?: any;
  size?: 'small' | 'medium';
  onChange?: any;
  type: string;
  readOnly?: boolean;
};

const renderLabel = (option: any) => option?.name || getFullName(option?.firstName, option?.lastName) || '';

const renderOption = (props: any, option: IUser) => {
  return (
    <li {...props} key={option?._id as string}>
      <ListItemAvatar>
        <AvatarMedia name={option?.name} avatar={option?.avatar} />
      </ListItemAvatar>
      <ListItemText primary={option.name || getFullName(option?.firstName, option?.lastName)} />
    </li>
  );
};

const ProvidersByTypeSelect = ({
  name,
  parentName,
  required,
  readOnly,
  multiple = false,
  label,
  helperText,
  disabled,
  type,
  setValue,
  onChange,
  ...props
}: ProvidersByTypeSelectProps) => {
  const providerType = useMemo(() => {
    return PROVIDERS_TYPES_MAP[type];
  }, [type]);

  const fetchFunc = useCallback(() => ProviderService.searchProvidersByType(providerType), [providerType]);

  const getOneFunc = useCallback(
    (id?: string) => {
      return ProviderService.getOneByType(id as string, providerType);
    },
    [providerType],
  );

  const handleChange = useCallback(
    (event: any) => {
      const { value } = event.target;

      if (!value) {
        setValue?.(`${name}`, null, { shouldValidate: true });
        if (parentName) {
          setValue?.(`${parentName}.ownershipName`, '', { shouldValidate: true });
        }
        return;
      }

      setValue?.(`${name}`, value?._id, { shouldValidate: true });
      if (parentName) {
        setValue?.(`${parentName}.ownershipName`, value?.name, { shouldValidate: true });
      }
    },
    [setValue, parentName, name],
  );

  return (
    <FormAsyncSelectAutocompleteField
      {...props}
      multiple={multiple}
      readOnly={readOnly}
      onChange={handleChange}
      required={required}
      label={label}
      name={name}
      disableCloseOnSelect={multiple}
      fetchFunc={fetchFunc}
      fetchValueFunc={multiple ? fetchFunc : getOneFunc}
      queryKey={`${type}_LIST_KEY`}
      autoHighlight
      isOptionEqualToValue={isOptionEqualToValue}
      id={'select-provider'}
      loadValue
      getOptionLabel={renderLabel}
      renderOption={renderOption}
      helperText={helperText}
      disabled={disabled || readOnly}
      sx={{
        backgroundColor: readOnly ? '#e5e7eb' : 'inherit',
        fontWeight: readOnly ? 500 : 400,
        cursor: readOnly ? 'pointer' : 'auto',
      }}
    />
  );
};

export default memo(ProvidersByTypeSelect);
